import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Alert, Collapse, Switch, Table, Tag, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faWifi, faBuildingShield, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// FLAT BUFFERS
import { RoleMenuT } from "../../../flatbuffers/generated/archive/role-menu";
import { MenuPageT } from "../../../flatbuffers/generated/archive/menu-page";
import { MenuActionT } from "../../../flatbuffers/generated/archive/menu-action";
// UTILS
import {
  actionTitle,
  formatPermissionLabel,
  PermissionMode,
  text,
  toggleTreeKeysBothModes,
  toggleTreeKeysOneMode,
} from "../../setupWizard/utils/roleMenu";
// INTERFACES
import { RootState } from "../../../app/store";
// VISUAL-ONLY "STRIP THIS PAGE'S DUPLICATED SUB_PAGES" PATCH
import { DUPLICATE_SUBPAGES_TREE_KEYS } from "./duplicateSubPagesPatch";

const { Panel } = Collapse;

const DUPLICATE_SUBPAGES_TREE_KEYS_SET = new Set(DUPLICATE_SUBPAGES_TREE_KEYS);

/**
 * A locked page/sub_page/action never becomes a row (or a Panel, or a block):
 * its switch could never be turned off here anyway (see renderSwitchCell's
 * `disabled`). Purely visual — never touches what gets serialised. Every
 * OTHER permission in the menu is shown; there is no hide-list.
 */
const isLockedNode = (node: { locked: boolean }): boolean => node.locked;

/**
 * TEMP PATCH (see duplicateSubPagesPatch.ts): a page whose tree_key is in
 * DUPLICATE_SUBPAGES_TREE_KEYS has its `sub_pages` emptied for display only —
 * everything else on the page (its own name/actions/counts) is untouched.
 *
 * Recursive: the matching page is not always a top-level entry in
 * `menu.pages` — e.g. `view-organization-management` actually lives nested
 * as a sub_page of `view-global` — so every level of the tree is walked, not
 * just the top-level pages array. A page that doesn't match itself still has
 * its own sub_pages walked the same way, in case a match is further down.
 * Applied once, right where the top-level pages are read for rendering, so
 * every downstream consumer (countGranted, pageHasVisibleContent, the
 * sub_page block loop, buildBlockRows/flattenPermissionRows) sees the
 * already-patched tree and stays in sync automatically.
 */
function stripDuplicateSubPages(page: MenuPageT): MenuPageT {
  const isDuplicate = DUPLICATE_SUBPAGES_TREE_KEYS_SET.has(text(page.treeKey));
  const hasSubPages = (page.subPages ?? []).length > 0;
  if (!isDuplicate && !hasSubPages) return page;
  const patched = page.clone();
  patched.subPages = isDuplicate ? [] : (page.subPages ?? []).map(stripDuplicateSubPages);
  return patched;
}

// Shared header styling for every column title (Description, Permission,
// Online, Offline) — one color/weight, defined once.
const COLUMN_TITLE_COLOR = "#2968c0";
function renderColumnTitle(label: string) {
  return <span style={{ color: COLUMN_TITLE_COLOR, fontWeight: "bolder" }}>{label}</span>;
}

/**
 * Same locked cascade as flattenPermissionRows/buildBlockRows: a locked action
 * is skipped, and a locked sub_page is skipped WHOLESALE (its own actions and
 * further-nested sub_pages never counted either). Without this filter the
 * badge counted tree nodes that never become a row in any table below it, so
 * its ratio didn't match what an operator could actually see.
 */
function countGranted(node: MenuPageT): { granted: number; total: number } {
  let total = 0;
  let granted = 0;
  (node.actions ?? [])
    .filter((a) => !isLockedNode(a))
    .forEach((a) => {
      total += 1;
      if (a.active || a.offline) granted += 1;
    });
  (node.subPages ?? [])
    .filter((sp) => !isLockedNode(sp))
    .forEach((sp) => {
      const nested = countGranted(sp);
      total += nested.total;
      granted += nested.granted;
    });
  return { granted, total };
}

/** Green once at least one item under this page/sub_page is granted, red while it's still zero. */
function renderCountTag(counts: { granted: number; total: number }) {
  return (
    <Tag className="permissionsCard__countTag" color={counts.granted > 0 ? "success" : "error"}>
      {counts.granted}/{counts.total}
    </Tag>
  );
}

interface PermissionRow {
  key: string;
  name: string;
  depth: number;
  node: MenuPageT | MenuActionT;
  description?: string;
}

/**
 * Per-permission `description` as sent by the backend, keyed by tree_key
 * (see collectMenuDescriptions). Empty map = no descriptions available.
 */
export type PermissionDescriptions = Record<string, string>;

/**
 * `description` is not a field on MenuPageT/MenuActionT — the backend sends it
 * on every menu page/sub_page/action, but the FlatBuffer schema has nowhere to
 * put it, so menuJsonToRoleMenuT drops it. It therefore reaches this component
 * as a separate tree_key -> description map built from the RAW menu JSON
 * (collectMenuDescriptions), and a row resolves its text by key.
 *
 * The untyped cast on the node is kept as the FIRST source so that the day the
 * schema does gain the field, the value carried on the node itself wins and
 * the side map can be deleted without touching any caller.
 */
function getDescription(
  node: MenuPageT | MenuActionT,
  treeKey: string,
  descriptions: PermissionDescriptions,
): string | undefined {
  return (node as unknown as { description?: string }).description || descriptions[treeKey];
}

/**
 * Flatten a node's descendants (sub_pages, recursively, and every action at
 * every level) into one ordered list of rows — no drill-down, everything is
 * listed at once. `depth` only drives indentation, it does not hide
 * anything else. A sub_page row's switches are still the same bulk toggle
 * over its descendants as before; a leaf action row's switches touch only
 * that one action. Does NOT include a row for `pages` themselves — the
 * caller lists those as siblings (recursing into a sub_page's own
 * sub_pages) or as a block's own header (the block's table only covers
 * what's *under* it).
 *
 * A locked sub_page/action is skipped entirely — never becomes a row, and a
 * locked sub_page's whole subtree is skipped with it (its own actions and
 * further-nested sub_pages never get the chance to become rows either). Same
 * cascade collectPageActionKeys already applies to a bulk toggle
 * (roleMenu.ts), kept consistent here for what's rendered at all: a locked
 * permission's switch can never be turned off in this table anyway (see
 * renderSwitchCell's `disabled`), so there is nothing an operator could do
 * with the row.
 */
function flattenPermissionRows(
  pages: MenuPageT[],
  depth: number,
  out: PermissionRow[],
  descriptions: PermissionDescriptions,
): void {
  pages.forEach((page) => {
    if (isLockedNode(page)) return;
    const pageKey = text(page.treeKey);
    out.push({
      key: pageKey,
      name: formatPermissionLabel(text(page.name)),
      depth,
      node: page,
      description: getDescription(page, pageKey, descriptions),
    });
    (page.actions ?? [])
      .filter((a) => !isLockedNode(a))
      .forEach((a) => {
        const actionKey = text(a.treeKey);
        out.push({
          key: actionKey,
          name: formatPermissionLabel(actionTitle(a, pageKey)),
          depth: depth + 1,
          node: a,
          description: getDescription(a, actionKey, descriptions),
        });
      });
    flattenPermissionRows(page.subPages ?? [], depth + 1, out, descriptions);
  });
}

/**
 * Rows for one independent block table: `node`'s own direct actions plus
 * everything under its own sub_pages, flattened. `node` itself is NOT a row
 * here — it's rendered as the block's header, above its table. A locked
 * direct action is dropped for the same reason flattenPermissionRows drops
 * locked sub_pages/actions — see its comment.
 */
function buildBlockRows(node: MenuPageT, descriptions: PermissionDescriptions): PermissionRow[] {
  const nodeKey = text(node.treeKey);
  const rows: PermissionRow[] = (node.actions ?? [])
    .filter((a) => !isLockedNode(a))
    .map((a) => {
      const actionKey = text(a.treeKey);
      return {
        key: actionKey,
        name: formatPermissionLabel(actionTitle(a, nodeKey)),
        depth: 0,
        node: a,
        description: getDescription(a, actionKey, descriptions),
      };
    });
  flattenPermissionRows(node.subPages ?? [], 0, rows, descriptions);
  return rows;
}

/** One visible sub_page's own precomputed block: its rows, built once per render. */
interface SubPageBlock {
  subPage: MenuPageT;
  subPageKey: string;
  rows: PermissionRow[];
}

/**
 * Everything the render loop needs for one top-level `page`, computed once.
 *
 * buildBlockRows is a recursive full-subtree flatten — expensive enough that
 * it must not run twice for the same sub_page on the same render. It used
 * to: once from the old pageHasVisibleContent's scan (deciding whether the
 * page's Panel renders at all) and again from the render loop building that
 * same sub_page's table. Computing every visible sub_page's rows here, once,
 * and deriving both the visibility decision AND the render data from this
 * one result removes that duplicate pass.
 *
 * `hasVisibleContent` mirrors the old pageHasVisibleContent: a direct action
 * that survives locked filtering, or at least one sub_page block with at least
 * one row of its own. A page whose every child is locked has neither, so its
 * Panel/Collapse entry is skipped rather than shown as an empty shell.
 */
interface PageRenderModel {
  page: MenuPageT;
  pageKey: string;
  counts: { granted: number; total: number };
  directActionRows: PermissionRow[];
  subPageBlocks: SubPageBlock[];
  hasVisibleContent: boolean;
}

function buildPageRenderModel(
  page: MenuPageT,
  descriptions: PermissionDescriptions,
): PageRenderModel {
  const pageKey = text(page.treeKey);
  // Defensive: today's real menus never put actions directly on a top-level
  // page (they always live under a sub_page), but the schema allows it —
  // give them their own tiny table so nothing is silently dropped if that
  // ever changes.
  const directActionRows: PermissionRow[] = (page.actions ?? [])
    .filter((a) => !isLockedNode(a))
    .map((a) => {
      const actionKey = text(a.treeKey);
      return {
        key: actionKey,
        name: formatPermissionLabel(actionTitle(a, pageKey)),
        depth: 0,
        node: a,
        description: getDescription(a, actionKey, descriptions),
      };
    });
  const subPageBlocks: SubPageBlock[] = (page.subPages ?? [])
    .filter((subPage) => !isLockedNode(subPage))
    .map((subPage) => ({
      subPage,
      subPageKey: text(subPage.treeKey),
      rows: buildBlockRows(subPage, descriptions),
    }));
  return {
    page,
    pageKey,
    counts: countGranted(page),
    directActionRows,
    subPageBlocks,
    hasVisibleContent:
      directActionRows.length > 0 || subPageBlocks.some((block) => block.rows.length > 0),
  };
}

/**
 * Inline permission editor for the Roles create form.
 *
 * Modeled on the same generated FlatBuffer Object API (RoleMenuT) as
 * WizardPermissionTreeEditor — JSON only appears at the REST boundary via
 * roleMenuTToMenuJson in the caller. This component intentionally does NOT
 * replace WizardPermissionTreeEditor: that one is still shared by the Edit
 * role drawer and the setup wizard's Users & Roles step, neither of which is
 * part of this refactor.
 *
 * Layout: each top-level `page` (menu.pages[i]) is a collapsible section
 * (collapsed by default — a role's full permission ceiling is large, nobody
 * wants every page's tables open at once). Its header carries only a
 * granted/total badge and its name — no standalone switches outside a table
 * anymore; bulk toggling lives entirely in each table's own column headers.
 * Inside, each direct `sub_page` gets its OWN independent block: its own
 * header and its own table, rather than one shared table for the whole page
 * — a page like edge-management (25 sub_pages) reads as 25 short tables
 * instead of one giant one. A block's table flattens everything under that
 * sub_page (its own actions and any further-nested sub_pages, recursively)
 * into rows indented by depth — no further collapse inside a block,
 * everything in it is visible at once. Every table's Online/Offline column
 * header carries its own select-all switch, scoped to just the rows in that
 * table — that IS the bulk toggle for a sub_page/block now.
 *
 * A row's switches are a read-only-by-design BULK TOGGLE over every action
 * nested under it (a sub_page row) — its active/offline flags always end up
 * being the OR of its descendants (see applyCheckedKeys), exactly like the
 * Tree editor's parent-checkbox cascade before it. A leaf action row's
 * switches touch only that one action. There is no independent parent state
 * that could disagree with its children.
 */
function RolesPermissionsCards({
  menu,
  onChange = () => {},
  readOnly = false,
  descriptions = {},
}: {
  menu: RoleMenuT | null;
  onChange?: (next: RoleMenuT) => void;
  readOnly?: boolean;
  /** tree_key -> backend description, shown in each row's Description tooltip. */
  descriptions?: PermissionDescriptions;
}) {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);

  // Per-table "enable online and offline columns" switch, keyed by the
  // table's own tree_key (a sub_page block key, or a page's own key for its
  // rare direct-actions table). Missing key = false = the default merged
  // "Permission" column; true = the split Online/Offline columns shown
  // before this feature existed. Scoped per table so opening one block's
  // columns has no effect on any other block.
  const [combinedColumns, setCombinedColumns] = useState<Record<string, boolean>>({});

  // Batches N key changes into ONE menu update — looping calls to a
  // single-key toggle would each read the same stale `menu` closure and
  // clobber each other's result instead of composing. See
  // toggleTreeKeysOneMode for the shared implementation (also used by the
  // Sidebar block's own switches).
  const toggleMany = (treeKeys: string[], mode: PermissionMode, nextValue: boolean) => {
    if (!menu) return;
    onChange(toggleTreeKeysOneMode(menu, treeKeys, mode, nextValue));
  };

  // Merged "Permission" column: one switch drives BOTH online and offline
  // together. See toggleTreeKeysBothModes for why this must chain two
  // applyCheckedKeys passes in one onChange rather than calling a
  // single-mode toggle twice in a row (the second call would silently undo
  // the first, both reading the same stale `menu` closure).
  const toggleManyBoth = (treeKeys: string[], nextValue: boolean) => {
    if (!menu) return;
    onChange(toggleTreeKeysBothModes(menu, treeKeys, nextValue));
  };

  const renderSwitchCell = (mode: PermissionMode) => (checked: boolean, record: PermissionRow) => {
    const { key, node } = record;
    return (
      <Switch
        size="small"
        checked={checked}
        disabled={readOnly || node.locked}
        onChange={(nextValue) => toggleMany([key], mode, nextValue)}
      />
    );
  };

  // One table's Online/Offline header: mode icon + label plus a select-all
  // switch scoped to just this table's own rows (not the whole page). Locked
  // rows are excluded from the keys the switch drives — their state isn't
  // theirs to bulk-toggle, same as their own per-row switch being disabled.
  const renderColumnHeader = (label: string, mode: PermissionMode, rows: PermissionRow[]) => {
    const rowKeys = rows.filter((row) => !row.node.locked).map((row) => row.key);
    const allChecked =
      rows.length > 0 &&
      rows.every((row) => (mode === "online" ? row.node.active : row.node.offline));
    return (
      <div
        className="permissionsCard__columnHeader"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <FontAwesomeIcon
          icon={mode === "online" ? faWifi : faBuildingShield}
          style={{ color: COLUMN_TITLE_COLOR }}
        />
        {renderColumnTitle(label)}
        {/* The select-all switch is a bulk EDIT control — read-only mode drops
            it entirely rather than showing a dead disabled toggle next to the
            column title. */}
        {!readOnly && (
          <Switch
            size="small"
            checked={allChecked}
            onChange={(nextValue) => toggleMany(rowKeys, mode, nextValue)}
          />
        )}
      </div>
    );
  };

  // Merged column header — same layout as renderColumnHeader (icon + label
  // + scoped select-all switch) but drives both modes at once via
  // toggleManyBoth. "Checked" for the header's select-all, and for each
  // row's own switch, means fully granted in BOTH modes — a row left mixed
  // from an earlier split-columns edit reads as unchecked here until the
  // switch is used again, rather than guessing which mode it "really" means.
  const renderCombinedColumnHeader = (rows: PermissionRow[]) => {
    const rowKeys = rows.filter((row) => !row.node.locked).map((row) => row.key);
    const allChecked = rows.length > 0 && rows.every((row) => row.node.active && row.node.offline);
    return (
      <div
        className="permissionsCard__columnHeader"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <FontAwesomeIcon icon={faWifi} style={{ color: COLUMN_TITLE_COLOR }} />
        <FontAwesomeIcon icon={faBuildingShield} style={{ color: COLUMN_TITLE_COLOR }} />
        {renderColumnTitle(t("organizationManagement.permission") || "Permission")}
        {!readOnly && (
          <Switch
            size="small"
            checked={allChecked}
            onChange={(nextValue) => toggleManyBoth(rowKeys, nextValue)}
          />
        )}
      </div>
    );
  };

  // One table's own "enable online and offline columns" switch — false
  // (the default) collapses Online/Offline into the single merged
  // Permission column above; true restores the previous 2-column layout.
  // Purely a display choice, not a permission edit, so it stays live even
  // in read-only view mode.
  const renderCombinedToggle = (tableKey: string) => (
    <span
      className="permissionsCard__combinedToggle"
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      <Switch
        size="small"
        checked={!!combinedColumns[tableKey]}
        onChange={(nextValue) => setCombinedColumns((prev) => ({ ...prev, [tableKey]: nextValue }))}
      />
      <span className="drawer__text permissionsCard__combinedToggleLabel">
        {t("organizationManagement.enableOnlineOfflineColumns") ||
          "Enable online and offline columns"}
      </span>
    </span>
  );

  const buildColumns = (rows: PermissionRow[], tableKey: string) => {
    const showSplitColumns = !!combinedColumns[tableKey];
    const columns: any[] = [
      {
        title: renderColumnTitle(t("organizationManagement.description") || "Description"),
        key: "description",
        width: 80,
        align: "center" as const,
        render: (_: unknown, record: PermissionRow) => (
          <Tooltip
            title={
              record.description ||
              t("organizationManagement.descriptionNotAdded") ||
              "description no added"
            }
          >
            <FontAwesomeIcon icon={faInfoCircle} style={{ color: COLUMN_TITLE_COLOR }} />
          </Tooltip>
        ),
      },
      {
        title: renderColumnTitle(t("organizationManagement.permission") || "Permission"),
        dataIndex: "name",
        key: "name",
        render: (name: string, record: PermissionRow) => (
          <span className="permissionsCard__nameCell" style={{ paddingLeft: record.depth * 20 }}>
            {name}
          </span>
        ),
      },
    ];

    // Default (switch off): one merged "Permission" column whose switch
    // drives online + offline together. Switch on: the previous 2-column
    // Online/Offline layout, unchanged.
    if (showSplitColumns) {
      columns.push(
        {
          title: renderColumnHeader(
            t("organizationManagement.onlinePermission") || "Online",
            "online",
            rows,
          ),
          dataIndex: ["node", "active"],
          key: "active",
          width: 250,
          align: "center" as const,
          render: renderSwitchCell("online"),
        },
        {
          title: renderColumnHeader(
            t("organizationManagement.offlinePermission") || "Offline",
            "offline",
            rows,
          ),
          dataIndex: ["node", "offline"],
          key: "offline",
          width: 250,
          align: "center" as const,
          render: renderSwitchCell("offline"),
        },
      );
    } else {
      columns.push({
        title: renderCombinedColumnHeader(rows),
        key: "permission",
        width: 250,
        align: "center" as const,
        render: (_: unknown, record: PermissionRow) => (
          <Switch
            size="small"
            checked={record.node.active && record.node.offline}
            disabled={readOnly || record.node.locked}
            onChange={(nextValue) => toggleManyBoth([record.key], nextValue)}
          />
        ),
      });
    }

    return columns;
  };

  // Read-only mode is handed the role's OWN granted menu, already pruned down
  // to what it holds — so "no pages" there means the role grants nothing, not
  // that no permission source was picked yet.
  if (readOnly && (!menu || (menu.pages ?? []).length === 0)) {
    return (
      <Alert
        type="info"
        showIcon
        message={
          t("organizationManagement.noPermissionsAssigned") ||
          "This role has no permissions assigned."
        }
      />
    );
  }

  if (!menu) {
    return <Alert type="info" showIcon message="Select a permission source to edit its menu." />;
  }

  return (
    <div>
      <Collapse ghost defaultActiveKey={[]}>
        {/* A locked page never becomes a Panel at all — same cascade as a
            locked sub_page/action never becoming a row (see
            flattenPermissionRows). A page that isn't itself locked but whose
            every child ended up locked is dropped too — an empty Panel with
            nothing under it is pure noise. */}
        {(menu.pages ?? [])
          .map(stripDuplicateSubPages)
          .filter((page) => !isLockedNode(page))
          .map((page) => buildPageRenderModel(page, descriptions))
          .filter((pageModel) => pageModel.hasVisibleContent)
          .map((pageModel) => {
            const { page, pageKey, counts, directActionRows, subPageBlocks } = pageModel;
            return (
              <Panel
                key={pageKey}
                className={`drawer__box__${theme} mb-3`}
                header={
                  <div>
                    {renderCountTag(counts)}
                    <FontAwesomeIcon
                      icon={faLock}
                      className="generalStyles__info generalStyles__mrFix"
                    />
                    <span>{formatPermissionLabel(text(page.name))}</span>
                  </div>
                }
              >
                {directActionRows.length > 0 && (
                  <div>
                    <div className="permissionsCard__header permissionsCard__block">
                      <div />
                      <div className="permissionsCard__toggles">
                        {renderCombinedToggle(`${pageKey}__direct`)}
                      </div>
                    </div>
                    <Table
                      className="my-custom-table"
                      size="small"
                      pagination={false}
                      rowKey="key"
                      columns={buildColumns(directActionRows, `${pageKey}__direct`)}
                      dataSource={directActionRows}
                    />
                  </div>
                )}
                {/* One independent table per sub_page — its own header (name +
                  switches + badge) directly above its own table, instead of
                  one shared table for the whole page. A locked sub_page is
                  skipped entirely (already excluded from subPageBlocks — see
                  buildPageRenderModel), and one left with zero rows after
                  that filtering renders no block at all rather than an empty
                  table. */}
                {subPageBlocks.map(({ subPage, subPageKey, rows: subPageRows }) => {
                  if (subPageRows.length === 0) return null;
                  const subPageCounts = countGranted(subPage);
                  return (
                    <div key={subPageKey} className="mb-3">
                      <div className="permissionsCard__header permissionsCard__block">
                        <div className="permissionsCard__title permissionsCard__blockTitle">
                          {renderCountTag(subPageCounts)}
                          <Tag color="blue" className="permissionsCard__tableTitleTag">
                            {formatPermissionLabel(text(subPage.name))}
                          </Tag>
                        </div>
                        <div className="permissionsCard__toggles">
                          {renderCombinedToggle(subPageKey)}
                        </div>
                      </div>
                      <Table
                        size="small"
                        pagination={false}
                        rowKey="key"
                        columns={buildColumns(subPageRows, subPageKey)}
                        dataSource={subPageRows}
                      />
                    </div>
                  );
                })}
              </Panel>
            );
          })}
      </Collapse>
    </div>
  );
}

export default RolesPermissionsCards;
